import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface CompatibilityFactor {
  name: string;
  score: number;
  icon: string;
}

interface CompatibilityScoreProps {
  overallScore: number;
  factors: CompatibilityFactor[];
}

const CompatibilityScore = ({ overallScore, factors }: CompatibilityScoreProps) => {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl border border-border space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-headline font-semibold text-foreground mb-2">
          Your Compatibility Score
        </h2>
        <p className="text-sm text-muted-foreground font-body">
          Based on your preferences and lifestyle
        </p>
      </div>

      <div className="flex flex-col items-center">
        <div className="relative w-40 h-40">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="80"
              cy="80"
              r="70"
              stroke="currentColor"
              strokeWidth="12"
              fill="none"
              className="text-muted"
            />
            <circle
              cx="80"
              cy="80"
              r="70"
              stroke="currentColor"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 70}`}
              strokeDashoffset={`${2 * Math.PI * 70 * (1 - overallScore / 100)}`}
              className={getScoreColor(overallScore)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-4xl font-headline font-bold ${getScoreColor(overallScore)}`}>
              {overallScore}%
            </span>
            <span className="text-sm text-muted-foreground font-body">Match</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {factors.map((factor, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name={factor.icon as any} size={18} variant="outline" className="text-primary" />
                <span className="text-sm font-body font-medium text-foreground">
                  {factor.name}
                </span>
              </div>
              <span className={`text-sm font-body font-semibold ${getScoreColor(factor.score)}`}>
                {factor.score}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`${getScoreBgColor(factor.score)} rounded-full h-2 transition-all duration-500`}
                style={{ width: `${factor.score}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-border">
        <p className="text-xs text-center text-muted-foreground font-body">
          This score is calculated based on your profile preferences, lifestyle, and experience level
        </p>
      </div>
    </div>
  );
};

export default CompatibilityScore;